import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Share2, Clock, Tag } from 'lucide-react';
import { useLanguage } from '../contexts/languageStore';
import { useWebsite } from '../contexts/websiteStore';
import { Link } from '../router/Link';
import { useRouter } from '../router/Router';

interface ArticleData {
    id: number;
    title: string;
    description: string;
    fullContent: string;
    category: 'company' | 'industry' | 'press';
    date: string;
    image: string;
    readTime: string;
    author?: string;
    tags?: string[];
}

export function ArticleDetailPage() {
    const { t, isRTL } = useLanguage();
    const { config, getAsset } = useWebsite();
    const { params } = useRouter();
    const [article, setArticle] = useState<ArticleData | null>(null);
    const [loading, setLoading] = useState(true);

    const articleId = params.articleId;

    // Function to render formatted text (bold, italic, etc.)
    const renderFormattedText = (text: string) => {
        // Bold text: **text** or __text__
        text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        text = text.replace(/__(.*?)__/g, '<strong>$1</strong>');

        // Italic text: *text* or _text_
        text = text.replace(/\*(.*?)\*/g, '<em>$1</em>');
        text = text.replace(/_(.*?)_/g, '<em>$1</em>');

        // Inline code: `code`
        text = text.replace(/`(.*?)`/g, '<code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">$1</code>');

        // Links: [text](url)
        text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">$1</a>');

        return <span dangerouslySetInnerHTML={{ __html: text }} />;
    };

    // Article data mapping
    const articlesData: Record<string, ArticleData> = {
        '1': {
            id: 1,
            title: t("news.articles.wasteManagement.title"),
            description: t("news.articles.wasteManagement.description"),
            fullContent: t("news.articles.wasteManagement.fullContent"),
            category: "company",
            date: "2025-08-14",
            image: getAsset(config.assets.news5 || 'news5.webp'),
            readTime: t("news.articles.wasteManagement.readTime"),
            author: t("news.articles.wasteManagement.author"),
            tags: [t("news.categories.company"), "waste management", "sustainability", "indonesia", "swiss technology"]
        }
    };

    useEffect(() => {
        // Scroll to top when component mounts or articleId changes
        window.scrollTo(0, 0);

        if (articleId && articlesData[articleId]) {
            setArticle(articlesData[articleId]);
        }
        setLoading(false);
    }, [articleId]);

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: article?.title,
                text: article?.description,
                url: window.location.href,
            });
        } else {
            // Fallback: copy to clipboard
            navigator.clipboard.writeText(window.location.href);
            // You could add a toast notification here
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-semibold text-gray-900 mb-4">{t("article.notFound.title")}</h1>
                    <p className="text-gray-600 mb-6">{t("article.notFound.description")}</p>
                    <Link to="/news" className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
                        <ArrowLeft className={`${isRTL ? 'ml-2 rotate-180' : 'mr-2'} h-4 w-4`} />
                        {t("article.notFound.backToNews")}
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pt-20">
            {/* Header */}
            <div className="bg-white shadow-sm pt-20">
                <div className="container mx-auto px-6 py-4">
                    <Link
                        to="/news"
                        className="inline-flex items-center text-gray-600 hover:text-primary transition-colors"
                    >
                        <ArrowLeft className={`${isRTL ? 'ml-2 rotate-180' : 'mr-2'} h-4 w-4`} />
                        {t("article.backToNews")}
                    </Link>
                </div>
            </div>

            {/* Article Content */}
            <div className="container mx-auto px-6 py-8 max-w-7xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    {/* Article Header */}
                    <div className="mb-12">
                        <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                            <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium">
                                {t(`news.categories.${article.category}`)}
                            </span>
                            <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <span>{new Date(article.date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4" />
                                <span>{article.readTime}</span>
                            </div>
                        </div>

                        <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-light text-gray-900 mb-8 leading-tight">
                            {article.title}
                        </h1>

                        <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 leading-relaxed max-w-4xl">
                            {article.description}
                        </p>

                        {article.author && (
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>{t("article.by")} {article.author}</span>
                            </div>
                        )}
                    </div>

                    {/* Featured Image */}
                    <div className="mb-16">
                        <img
                            src={article.image}
                            alt={article.title}
                            className="w-full h-96 md:h-[600px] lg:h-[700px] xl:h-[800px] object-cover rounded-2xl shadow-2xl"
                        />
                    </div>

                    {/* Article Body */}
                    <div className="bg-white rounded-2xl p-8 md:p-16 shadow-xl">
                        <div className="prose prose-lg max-w-none">
                            <div className="text-gray-700 leading-relaxed text-lg grid grid-cols-1 lg:grid-cols-3 gap-12">
                                {/* Main Content */}
                                <div className="lg:col-span-2">
                                    {article.fullContent.split('\n').map((paragraph, index) => {
                                        // Check if this line contains an image
                                        const imageMatch = paragraph.match(/!\[([^\]]*)\]\(([^)]+)\)/);
                                        if (imageMatch) {
                                            const [, alt, src] = imageMatch;

                                            // Check if this is one of the images that should be large
                                            const largeImages = [
                                                'Renewable Energy Impact',
                                                'Recycling Infrastructure',
                                                'Waste Processing Operations',
                                                'Waste Management Facility',
                                                'تأثير الطاقة المتجددة',
                                                'بنية إعادة التدوير',
                                                'عمليات معالجة النفايات',
                                                'منشأة إدارة النفايات'
                                            ];

                                            const isLargeImage = largeImages.some(largeImage => alt.includes(largeImage));

                                            const imageClass = isLargeImage
                                                ? "w-full max-w-4xl mx-auto object-contain"
                                                : "w-full max-w-lg mx-auto object-contain";

                                            const imageStyle = isLargeImage
                                                ? {
                                                    minHeight: '300px',
                                                    maxHeight: '600px',
                                                    filter: 'none',
                                                    backgroundColor: 'transparent'
                                                }
                                                : {
                                                    minHeight: '150px',
                                                    maxHeight: '300px',
                                                    filter: 'none',
                                                    backgroundColor: 'transparent'
                                                };

                                            return (
                                                <div key={index} className="my-16">
                                                    <div className="relative">
                                                        <img
                                                            src={src}
                                                            alt={alt}
                                                            className={imageClass}
                                                            style={imageStyle}
                                                        />
                                                    </div>
                                                    {alt && (
                                                        <p className="text-center text-gray-600 mt-4 text-sm italic">
                                                            {alt}
                                                        </p>
                                                    )}
                                                </div>
                                            );
                                        }

                                        // Check for headings
                                        const headingMatch = paragraph.match(/^(#{1,6})\s+(.+)$/);
                                        if (headingMatch) {
                                            const [, hashes, text] = headingMatch;
                                            const level = hashes.length;

                                            const headingClass = `mb-8 mt-12 font-bold text-gray-900 border-l-4 border-primary pl-6 ${level === 1 ? 'text-2xl sm:text-3xl md:text-4xl' :
                                                level === 2 ? 'text-xl sm:text-2xl md:text-3xl' :
                                                    level === 3 ? 'text-lg sm:text-xl md:text-2xl' :
                                                        level === 4 ? 'text-base sm:text-lg md:text-xl' :
                                                            level === 5 ? 'text-sm sm:text-base md:text-lg' : 'text-xs sm:text-sm md:text-base'
                                                }`;

                                            if (level === 1) {
                                                return <h1 key={index} className={headingClass}>{renderFormattedText(text)}</h1>;
                                            } else if (level === 2) {
                                                return <h2 key={index} className={headingClass}>{renderFormattedText(text)}</h2>;
                                            } else if (level === 3) {
                                                return <h3 key={index} className={headingClass}>{renderFormattedText(text)}</h3>;
                                            } else if (level === 4) {
                                                return <h4 key={index} className={headingClass}>{renderFormattedText(text)}</h4>;
                                            } else if (level === 5) {
                                                return <h5 key={index} className={headingClass}>{renderFormattedText(text)}</h5>;
                                            } else {
                                                return <h6 key={index} className={headingClass}>{renderFormattedText(text)}</h6>;
                                            }
                                        }

                                        // Regular paragraph with formatting
                                        if (paragraph.trim()) {
                                            return (
                                                <p key={index} className="mb-8 leading-9 text-base sm:text-lg">
                                                    {renderFormattedText(paragraph)}
                                                </p>
                                            );
                                        }

                                        // Empty line
                                        return <br key={index} />;
                                    })}
                                </div>

                                {/* Sidebar */}
                                <div className="lg:col-span-1">
                                    <div className="sticky top-8">
                                        <div className="bg-gray-50 rounded-xl p-6">
                                            <h3 className="text-lg font-semibold text-gray-900 mb-4">Article Info</h3>
                                            <div className="space-y-3 text-sm text-gray-600">
                                                <div className="flex items-center gap-2">
                                                    <Calendar className="h-4 w-4" />
                                                    <span>{new Date(article.date).toLocaleDateString()}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Clock className="h-4 w-4" />
                                                    <span>{article.readTime}</span>
                                                </div>
                                                {article.author && (
                                                    <div className="flex items-center gap-2">
                                                        <span>{t("article.by")} {article.author}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {article.tags && article.tags.length > 0 && (
                                                <div className="mt-6">
                                                    <h4 className="text-sm font-medium text-gray-700 mb-3">{t("article.tags")}:</h4>
                                                    <div className="flex flex-wrap gap-2">
                                                        {article.tags.map((tag, index) => (
                                                            <span
                                                                key={index}
                                                                className="px-3 py-1 bg-white text-gray-700 text-xs rounded-full border"
                                                            >
                                                                {tag}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            <div className="mt-6 pt-6 border-t border-gray-200">
                                                <button
                                                    onClick={handleShare}
                                                    className="w-full inline-flex items-center justify-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                                                >
                                                    <Share2 className={`${isRTL ? 'ml-2' : 'mr-2'} h-4 w-4`} />
                                                    {t("article.share")}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>


                    </div>
                </motion.div>
            </div>
        </div>
    );
} 